import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useConsent } from "../lib/queries";
import { getCurrentUserClaims } from "../lib/auth";
import { LoadingState, ErrorState } from "../components/Loading";
import { PageShell } from "../components/PageShell";
import { SettingsTabs } from "../components/SettingsTabs";
import { ThemeToggle } from "../components/ThemeToggle";
import { ChangePasswordForm } from "../components/ChangePasswordForm";
import { DataRights } from "../components/DataRights";
import { PricingSettings } from "../components/PricingSettings";
import { DisplayNameSetting } from "../components/DisplayNameSetting";

interface ConsentStatus {
  privacy_policy_accepted_at: string | null;
  tos_accepted_at: string | null;
  parental_consent_accepted_at: string | null;
}

function formatTimestamp(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

/**
 * The coach's own account (PRD §2.1).
 *
 * There was no coach-facing settings surface at all: the nav was Clients and Library, `/family/settings`
 * existed, and a coach could neither see their own account details nor change their own password. The
 * only password change in the whole app was the emailed forgot-password code, so rotating a password
 * meant pretending to have forgotten it.
 *
 * Deliberately not a mirror of the family page. A coach has no subscription of their own (there is no
 * self-serve coach signup, and adding one would need a coach-side payment plan first), so there is no
 * billing section to show -- inventing a disabled one would imply a feature that doesn't exist.
 */
export function CoachSettings() {
  const [claims, setClaims] = useState<{ email: string | null; name: string | null; username: string | null } | null>(null);
  const consentQuery = useConsent<ConsentStatus>();

  useEffect(() => {
    let cancelled = false;
    void getCurrentUserClaims().then((result) => {
      if (!cancelled) setClaims(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const consent = consentQuery.data;
  const privacyAccepted = formatTimestamp(consent?.privacy_policy_accepted_at ?? null);
  const tosAccepted = formatTimestamp(consent?.tos_accepted_at ?? null);
  const hasConsentOnFile = Boolean(privacyAccepted || tosAccepted);

  const accountTabContent = (
    <>
      <h2>Your account</h2>
      {claims === null ? (
        <LoadingState label="Loading your account…" />
      ) : (
        <ul className="goal-list">
          {/* The Cognito `name` claim is deliberately no longer shown. It was a second name for the same
              thing -- families never see it, and displaying it beside the one they do see meant a coach
              read "Beta Coach" here while signing every invite as "Coach Sam". The editable one below
              is the one that reaches people. */}
          <li>Username: {claims.username ?? "—"}</li>
          <li>Email: {claims.email ?? "—"}</li>
          <li>Role: Coach</li>
        </ul>
      )}

      <DisplayNameSetting />

      <h2>Password</h2>
      <ChangePasswordForm />

      <h2>Consent</h2>
      {consentQuery.isPending && <LoadingState label="Loading your consent record…" />}
      {/* A failed consent read is worth reporting but must not take the page down -- the account details
          and the password form above it are independent and still usable. */}
      {consentQuery.isError && <ErrorState error={consentQuery.error} onRetry={() => consentQuery.refetch()} />}
      {consentQuery.isSuccess &&
        (hasConsentOnFile ? (
          <ul className="goal-list">
            {privacyAccepted && <li>Privacy Policy: {privacyAccepted}</li>}
            {tosAccepted && <li>Terms of Service: {tosAccepted}</li>}
          </ul>
        ) : (
          /* Said rather than shown as "Not yet accepted", which would read as something the coach had
             skipped. A coach account is created by hand and never passes through the signup flow that
             records consent, so there is genuinely nothing on file and nothing they can do about it. */
          <p className="form-hint">
            No consent record on file. Coach accounts are created directly rather than through the signup
            flow, which is where consent is captured.
          </p>
        ))}
    </>
  );

  return (
    <PageShell
      title="Account settings"
      actions={
        <Link className="link-button" to="/dashboard">
          Back to clients
        </Link>
      }
    >
      <SettingsTabs
        defaultTabId="billing"
        tabs={[
          {
            id: "billing",
            label: "Billing",
            // What families pay is the coach's to set (finding #15) -- it was a deploy-time constant.
            content: <PricingSettings />,
          },
          { id: "account", label: "Account", content: accountTabContent },
          {
            id: "appearance",
            label: "Appearance",
            content: (
              <>
                <h2>Appearance</h2>
                <ThemeToggle />
              </>
            ),
          },
          {
            id: "your-data",
            label: "Your data",
            // Export only. A coach account owns every client on the roster, so deleting it would take
            // other people's data with it -- the server refuses, and the button isn't offered rather
            // than being rendered and then rejected.
            content: <DataRights canDelete={false} />,
          },
        ]}
      />
    </PageShell>
  );
}
